import Phaser from "phaser";
import Arrow from "../Arrow";
import Bow from "../Bow";
import CharacterAttributes from "../Character/CharacterAttributes";
import { DamageMath } from "../Math/DamageMath";
import MathHelpers from "../Math/MathHelpers";
import { IArrowCaster } from "../Skills/IArrowCaster";
import { ArrowSpawnConfig } from "../Skills/ArrowSpawnConfig";
import ArrowContainer from "./ArrowContainer";

export default class ArrowCaster implements IArrowCaster {
  private mScene: Phaser.Scene;
  private mContainer: ArrowContainer;
  private mBowGetter: () => Bow;
  private mAttribsGetter: () => CharacterAttributes;
  private mOriginGetter: () => { x: number; y: number };
  private mAimPositionGetter: () => { x: number; y: number };

  constructor(
    scene: Phaser.Scene,
    container: ArrowContainer,
    bowGetter: () => Bow,
    attribsGetter: () => CharacterAttributes,
    originGetter: () => { x: number; y: number },
    aimPositionGetter: () => { x: number; y: number }
  ) {
    this.mScene = scene;
    this.mContainer = container;
    this.mBowGetter = bowGetter;
    this.mAttribsGetter = attribsGetter;
    this.mOriginGetter = originGetter;
    this.mAimPositionGetter = aimPositionGetter;
  }

  public GetOriginX(): number {
    return this.mOriginGetter().x;
  }

  public GetOriginY(): number {
    return this.mOriginGetter().y;
  }

  public GetAimAngle(): number {
    const aim = this.mAimPositionGetter();
    const origin = this.mOriginGetter();
    return (
      Math.atan2(aim.x - origin.x, -(aim.y - origin.y)) *
        (180 / Math.PI) -
      180
    );
  }

  public SpawnArrow(config: ArrowSpawnConfig): void {
    this.spawnArrowInternal(config);
  }

  public ScheduleArrow(config: ArrowSpawnConfig, delayMs: number): void {
    if (delayMs <= 0) {
      this.spawnArrowInternal(config);
    } else {
      this.mScene.time.delayedCall(delayMs, () => {
        this.spawnArrowInternal(config);
      });
    }
  }

  private spawnArrowInternal(config: ArrowSpawnConfig): void {
    const scene = this.mScene;
    const origin = this.mOriginGetter();
    const bow = this.mBowGetter();
    const attribs = this.mAttribsGetter();

    const spriteArrow = scene.physics.add.sprite(origin.x, origin.y, "arrow");
    spriteArrow.setScale(0.5, 0.5);

    const arrow = new Arrow(spriteArrow);
    this.mContainer.Add(arrow);

    arrow.Init(config.angle);

    const bowDamage = MathHelpers.RandomInteger(bow.MinBaseDamage, bow.MaxBaseDamage);
    const calculated = DamageMath.calculateArrowDamage(
      attribs.Strength,
      attribs.Dexterity,
      bowDamage,
      1.0
    );

    arrow.Damage = Math.trunc(calculated.Damage * config.damageMultiplier);
    arrow.IsCritical = calculated.CriticalHit;
    arrow.IsExploding = config.isExploding;
    arrow.ExplosionRadius = config.explosionRadius;
    arrow.ExplosionDamageMultiplier = config.explosionDmgMult;
    arrow.IsPiercing = config.isPiercing ?? false;

    // Compute target point from angle, using aim position as base direction
    const aim = this.mAimPositionGetter();
    const dx = aim.x - origin.x;
    const dy = aim.y - origin.y;
    const baseAngle = Math.atan2(dx, -dy);
    const targetAngle = (config.angle + 180) * (Math.PI / 180);
    const offsetAngle = targetAngle - baseAngle;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const targetX = origin.x + Math.sin(baseAngle + offsetAngle) * dist;
    const targetY = origin.y - Math.cos(baseAngle + offsetAngle) * dist;

    arrow.ShootArrow(targetX, targetY, origin.x, origin.y, config.speedMultiplier);

    spriteArrow.setPosition(origin.x, origin.y);
    spriteArrow.setBounce(0);
    spriteArrow.setCollideWorldBounds(true);
  }
}
